/**
 * QC Report — File: sparseness / density analysis.
 *
 * Direct port of `src/lib/data-hub/qc-file-report.ts`. Pure: given the rubric
 * from `metric_definitions` plus the staged rows of one import, it answers "how
 * much of what we could have measured did this file actually contain?".
 *
 *   Factors      M metrics · C categories · A accounts · D distinct dates
 *   Overall      actual / (M × A × D)
 *   By metric    points(m) / (A × D)
 *   By date      points(d) / (M × A)
 *   By category  points(k) / (M_k × A × D_k), D_k being the dates on which any
 *                metric in that category was measured
 *   By account   points(a) / (M × D)
 *
 * The denominators differ per view on purpose: each asks what the maximum would
 * be if that slice were fully populated. Using one global denominator would
 * make every row read the same and say nothing.
 *
 * "Relationships Alignment" is excluded from the rubric, matching the reference
 * — those metrics are relationship data, not measurable KPI coverage.
 */

export type RubricMetric = {
  metric_id: string;
  metric_name: string;
  category: string | null;
};

export type SparsenessRow = {
  key: string;
  label: string;
  actual: number;
  max: number;
  density: number;
};

export type SparsenessReport = {
  factors: {
    metrics_in_rubric: number;
    categories_in_rubric: number;
    accounts_in_file: number;
    distinct_dates_in_file: number;
  };
  overall: { actual: number; max: number; density: number };
  by_metric: SparsenessRow[];
  by_date: SparsenessRow[];
  by_category: SparsenessRow[];
  by_account: SparsenessRow[];
};

export type StagingPoint = {
  account_name: string | null;
  metric_id: string | null;
  measurement_date: string | null;
  category?: string | null;
};

function pickDate(r: StagingPoint): string | null {
  const d = r.measurement_date;
  return d ? d.slice(0, 10) : null;
}

const EXCLUDED_CATEGORIES = new Set([
  "relationship alignment",
  "relationships alignment",
]);

function isRubricEligible(m: RubricMetric): boolean {
  return !EXCLUDED_CATEGORIES.has((m.category ?? "").trim().toLowerCase());
}

export function computeSparseness(
  rubric: RubricMetric[],
  rows: StagingPoint[]
): SparsenessReport {
  const rubricMetrics = rubric.filter(isRubricEligible);
  const M = rubricMetrics.length;

  const rubricCategories = new Set(
    rubricMetrics.map(m => (m.category ?? "").trim()).filter(c => c.length > 0)
  );
  const C = rubricCategories.size;

  const metricToCategory = new Map<string, string>();
  rubricMetrics.forEach(m =>
    metricToCategory.set(m.metric_id, (m.category ?? "").trim())
  );

  // A "point" is one measurable observation: it needs an account, a metric and
  // a date. Anything missing one of those cannot contribute to coverage.
  const accounts = new Set<string>();
  const dates = new Set<string>();
  const points: StagingPoint[] = [];
  rows.forEach(r => {
    const d = pickDate(r);
    if (!d || !r.account_name || !r.metric_id) return;
    accounts.add(r.account_name);
    dates.add(d);
    points.push(r);
  });

  const A = accounts.size;
  const D = dates.size;
  const N = points.length;

  const overallMax = M * A * D;
  const overall = {
    actual: N,
    max: overallMax,
    density: overallMax > 0 ? N / overallMax : 0,
  };

  const perMetric = new Map<string, number>();
  points.forEach(p => {
    if (p.metric_id)
      perMetric.set(p.metric_id, (perMetric.get(p.metric_id) ?? 0) + 1);
  });
  const by_metric: SparsenessRow[] = rubricMetrics
    .map(m => {
      const actual = perMetric.get(m.metric_id) ?? 0;
      const max = A * D;
      return {
        key: m.metric_id,
        label: m.metric_name,
        actual,
        max,
        density: max ? actual / max : 0,
      };
    })
    .sort((a, b) => b.density - a.density);

  const perDate = new Map<string, number>();
  points.forEach(p => {
    const d = pickDate(p);
    if (d) perDate.set(d, (perDate.get(d) ?? 0) + 1);
  });
  const by_date: SparsenessRow[] = Array.from(perDate.entries())
    .map(([d, actual]) => ({
      key: d,
      label: d,
      actual,
      max: M * A,
      density: M * A ? actual / (M * A) : 0,
    }))
    // Newest first, matching the reference.
    .sort((a, b) => (a.key < b.key ? 1 : -1));

  const perCategoryPoints = new Map<string, number>();
  const perCategoryDates = new Map<string, Set<string>>();
  const perCategoryMetricCount = new Map<string, number>();
  rubricMetrics.forEach(m => {
    const c = (m.category ?? "").trim();
    if (!c) return;
    perCategoryMetricCount.set(c, (perCategoryMetricCount.get(c) ?? 0) + 1);
  });
  points.forEach(p => {
    const c = metricToCategory.get(p.metric_id ?? "");
    if (!c) return;
    perCategoryPoints.set(c, (perCategoryPoints.get(c) ?? 0) + 1);
    const d = pickDate(p);
    if (d) {
      const s = perCategoryDates.get(c) ?? new Set<string>();
      s.add(d);
      perCategoryDates.set(c, s);
    }
  });
  const by_category: SparsenessRow[] = Array.from(rubricCategories)
    .map(c => {
      const actual = perCategoryPoints.get(c) ?? 0;
      const Mk = perCategoryMetricCount.get(c) ?? 0;
      const Dk = perCategoryDates.get(c)?.size ?? 0;
      const max = Mk * A * Dk;
      return { key: c, label: c, actual, max, density: max ? actual / max : 0 };
    })
    .sort((a, b) => b.density - a.density);

  const perAccount = new Map<string, number>();
  points.forEach(p => {
    if (p.account_name)
      perAccount.set(p.account_name, (perAccount.get(p.account_name) ?? 0) + 1);
  });
  const by_account: SparsenessRow[] = Array.from(accounts)
    .map(a => {
      const actual = perAccount.get(a) ?? 0;
      const max = M * D;
      return { key: a, label: a, actual, max, density: max ? actual / max : 0 };
    })
    .sort((a, b) => b.density - a.density);

  return {
    factors: {
      metrics_in_rubric: M,
      categories_in_rubric: C,
      accounts_in_file: A,
      distinct_dates_in_file: D,
    },
    overall,
    by_metric,
    by_date,
    by_category,
    by_account,
  };
}
